import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Reusable bulk-selection state for list/table screens.
 *
 * Contract:
 *   • Selection is a Set<string> of record ids.
 *   • Selection PERSISTS across filter changes, pagination, sort, search —
 *     the parent is responsible for calling `pruneToKnown(allKnownIds)`
 *     if it wants selection to also drop entries no longer resolvable.
 *   • `selectAllVisible` toggles ONLY the ids currently shown after the
 *     caller's filters are applied.
 *   • `allVisibleSelected` reflects the state of the currently rendered
 *     rows; it doesn't care about hidden selections.
 *
 * The hook does NOT reset when the input `visibleIds` array changes so
 * that selecting a row, then navigating to page 2, still counts that row.
 */
export function useBulkSelection(visibleIds) {
    const [selected, setSelected] = useState(() => new Set());

    const toggle = useCallback((id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const set = useCallback((id, on) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });
    }, []);

    const selectAllVisible = useCallback(() => {
        setSelected((prev) => {
            const next = new Set(prev);
            visibleIds.forEach((id) => next.add(id));
            return next;
        });
    }, [visibleIds]);

    const clearVisible = useCallback(() => {
        setSelected((prev) => {
            const next = new Set(prev);
            visibleIds.forEach((id) => next.delete(id));
            return next;
        });
    }, [visibleIds]);

    const clearAll = useCallback(() => setSelected(new Set()), []);

    const allVisibleSelected = useMemo(() => {
        if (!visibleIds || visibleIds.length === 0) return false;
        return visibleIds.every((id) => selected.has(id));
    }, [visibleIds, selected]);

    const someVisibleSelected = useMemo(() => {
        if (!visibleIds || visibleIds.length === 0) return false;
        return visibleIds.some((id) => selected.has(id));
    }, [visibleIds, selected]);

    const toggleAllVisible = useCallback(() => {
        if (allVisibleSelected) clearVisible();
        else selectAllVisible();
    }, [allVisibleSelected, clearVisible, selectAllVisible]);

    const has = useCallback((id) => selected.has(id), [selected]);
    const ids = useMemo(() => Array.from(selected), [selected]);

    /** Drop any selected ids no longer present in the known corpus (e.g.
     * after a successful bulk delete). Idempotent; safe to call from a
     * useEffect that watches the parent list. */
    const pruneToKnown = useCallback((allKnownIds) => {
        setSelected((prev) => {
            if (prev.size === 0) return prev;
            const known = new Set(allKnownIds);
            const next = new Set();
            prev.forEach((id) => { if (known.has(id)) next.add(id); });
            return next.size === prev.size ? prev : next;
        });
    }, []);

    return {
        ids,
        count: selected.size,
        has,
        toggle,
        set,
        clearAll,
        selectAllVisible,
        toggleAllVisible,
        allVisibleSelected,
        someVisibleSelected,
        pruneToKnown,
    };
}
